import { useMemo, useState } from 'react';
import { Box, Button, Dialog, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { SIDEBAR_GROUND } from '../../components/Frame';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import type { Opportunity } from '../../shared/opportunity';
import type { Trims } from './attendance';
import { buildDayRoute, type Home } from './dayRoute';
import DayRouteMap from './DayRouteMap';
import PlaceField from './PlaceField';
import type { HomeStore } from './useHome';

/**
 * MAP MY DAY, in a right sidebar: under a rule, the day's scheduled events as
 * a route, from the user's hotel and back once they have said where it is.
 *
 * The hotel is never asked for up front, but it is always *offered*: with
 * nothing to map — an empty day, or no day open at all — the section stays and
 * holds the offer alone (Sam, Sept 19). Someone who has just landed on the site
 * has no events yet and is exactly the person who wants to set it, and until
 * now that was the one state in which the sidebar said nothing.
 */

const Rule = styled('hr')({
  margin: '28px 0 0',
  border: 0,
  borderTop: '1px solid rgba(243,238,231,0.35)',
});

/** The hotel's offer drops under the label when a narrow sidebar has no room for both. */
const Heading = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  columnGap: 12,
  rowGap: 6,
  margin: '22px 0 12px',
});

const Label = styled('div')({
  fontFamily: FONTS.body,
  fontSize: 11.5,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'rgba(243,238,231,0.55)',
});

const Note = styled('p')({
  margin: 0,
  fontFamily: FONTS.body,
  fontSize: 13,
  lineHeight: 1.5,
  color: 'rgba(243,238,231,0.7)',
  '& b': { fontWeight: 700, color: BRAND.parchment },
});

/** Quiet: an offer, not a step. */
const HotelButton = styled('button')({
  appearance: 'none',
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 12,
  color: 'rgba(243,238,231,0.7)',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  whiteSpace: 'nowrap',
  '&:hover': { color: BRAND.parchment },
});

interface Props {
  /** The day's scheduled events. */
  events: Opportunity[];
  trims: Trims;
  home: HomeStore;
  onOpenEvent: (event: Opportunity) => void;
  /** Said after the label, for a sidebar that does not otherwise name the day. */
  dayLabel?: string;
  /** A hovered card: put into the route at its time, for as long as the pointer stays. */
  preview?: Opportunity | null;
}

export default function MapMyDay({ events, trims, home, onOpenEvent, dayLabel, preview = null }: Props) {
  const [editing, setEditing] = useState(false);
  const route = useMemo(
    () =>
      buildDayRoute(
        preview && !events.some((e) => e.id === preview.id) ? [...events, preview] : events,
        trims,
        home.home,
      ),
    [events, preview, trims, home.home],
  );
  const onRoute = route.stops.some((s) => !s.home);

  const nothingToMap = !onRoute && route.offRoute.length === 0;

  const dialog = (
    <HotelDialog
      open={editing}
      current={home.home}
      onClose={() => setEditing(false)}
      onSave={(next) => {
        home.setHome(next);
        setEditing(false);
      }}
    />
  );

  // Nothing on this day, or no day at all: the offer, and nothing else.
  if (nothingToMap) {
    return (
      <>
        <Rule />
        <Heading>
          <Label>Map my day</Label>
          <HotelButton type="button" onClick={() => setEditing(true)}>
            {home.home ? 'Change hotel' : 'Add my hotel'}
          </HotelButton>
        </Heading>
        <Note>
          {home.home ? (
            <>
              Every day starts and ends at <b>{home.home.label}</b>. Add events and the route
              between them appears here.
            </>
          ) : (
            'Say where you’re staying and every day is mapped from there, with the travel time between events.'
          )}
        </Note>
        {dialog}
      </>
    );
  }

  return (
    <>
      <Rule />
      <Heading>
        <Label>
          Map my day{dayLabel && <Box component="span" sx={{ opacity: 0.8 }}> · {dayLabel}</Box>}
        </Label>
        {/* Offered whatever the day holds: someone with one event still has a
            hotel, and the route from it is the point. */}
        <HotelButton type="button" onClick={() => setEditing(true)}>
          {home.home ? 'Change hotel' : 'Start from my hotel'}
        </HotelButton>
      </Heading>

      <DayRouteMap
        route={route}
        previewId={preview?.id}
        homeLabel={home.home?.label}
        onOpenEvent={onOpenEvent}
        onEditHome={() => setEditing(true)}
      />

      {dialog}
    </>
  );
}

/** Where the user is staying: one field, with the address suggestions the event forms use. */
function HotelDialog({
  open,
  current,
  onClose,
  onSave,
}: {
  open: boolean;
  current: Home | null;
  onClose: () => void;
  /** Null forgets it. */
  onSave: (home: Home | null) => void;
}) {
  const [label, setLabel] = useState('');
  const [coords, setCoords] = useState<[number, number] | null>(null);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      // Starts from what is set each time it opens.
      slotProps={{
        transition: {
          onEnter: () => {
            setLabel(current?.label ?? '');
            setCoords(current?.coords ?? null);
          },
        },
        paper: {
          sx: { background: SIDEBAR_GROUND, backgroundImage: 'none', borderRadius: 3, p: 3, width: 400, maxWidth: 'calc(100vw - 32px)' },
        },
      }}
    >
      <Typography sx={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 19, mb: 0.5, color: BRAND.parchment }}>
        Where are you staying?
      </Typography>
      <Typography sx={{ fontSize: 12.5, opacity: 0.65, mb: 2, color: BRAND.parchment }}>
        Map my day will start and end every day there. It stays on this device.
      </Typography>

      <PlaceField
        label="Hotel or address"
        placeholder="Start typing a hotel or an address"
        value={label}
        coords={coords}
        onChange={(venue, at) => {
          setLabel(venue);
          setCoords(at);
        }}
        hint={(located) => (located ? 'Found on the map.' : 'Pick a suggestion to put it on the map.')}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5 }}>
        {current && (
          <Button onClick={() => onSave(null)} sx={{ color: 'rgba(243,238,231,0.7)', fontWeight: 600, mr: 'auto' }}>
            Forget my hotel
          </Button>
        )}
        <Button onClick={onClose} sx={{ color: 'rgba(243,238,231,0.7)', fontWeight: 600, ml: current ? 0 : 'auto' }}>
          Cancel
        </Button>
        <Button
          disabled={!coords || !label.trim()}
          onClick={() => coords && onSave({ label: label.trim(), coords })}
          sx={{
            background: BRAND.parchment,
            color: BRAND.mocha,
            fontWeight: 700,
            borderRadius: 999,
            px: 3,
            '&:hover': { background: '#fff' },
            '&.Mui-disabled': { background: 'rgba(243,238,231,0.25)', color: 'rgba(54,47,42,0.6)' },
          }}
        >
          Save
        </Button>
      </Box>
    </Dialog>
  );
}
